/**
 * StageDeleteModal — deleting a stage that still holds candidates requires
 * choosing where they go. System stages (automation targets) carry a warning.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/Modal';
import type { Stage } from '@/features/pipeline/types';

export function StageDeleteModal({
  stage,
  stages,
  count,
  busy = false,
  onConfirm,
  onClose,
}: {
  stage: Stage | null;
  stages: Stage[];
  count: number;
  busy?: boolean;
  onConfirm: (moveTo: string | undefined) => void;
  onClose: () => void;
}) {
  const [moveTo, setMoveTo] = useState('');
  // A destination picked for one stage must not carry over to the next.
  useEffect(() => setMoveTo(''), [stage?.id]);
  const others = stages.filter((s) => s.id !== stage?.id);
  const needsDestination = count > 0;
  const canConfirm = !busy && (!needsDestination || others.some((s) => s.id === moveTo));

  return (
    <Modal
      open={!!stage}
      onClose={onClose}
      title={`Delete “${stage?.name ?? ''}”?`}
      description={
        needsDestination
          ? `${count} candidate${count === 1 ? '' : 's'} ${count === 1 ? 'is' : 'are'} in this stage. Pick where they go first.`
          : 'The stage is empty, so nothing else changes.'
      }
      initialFocus={needsDestination ? '#delete-move-to' : undefined}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-10 items-center justify-center rounded-md border border-gray-200 bg-white px-3.5 text-sm font-medium text-gray-800 hover:bg-page"
          >
            Keep stage
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => onConfirm(moveTo || undefined)}
            className="inline-flex min-h-10 items-center justify-center rounded-md bg-danger px-3.5 text-sm font-medium text-white hover:bg-danger-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Deleting…' : needsDestination ? 'Move candidates and delete' : 'Delete stage'}
          </button>
        </>
      }
    >
      {needsDestination && (
        <label className="block text-sm">
          <span className="font-medium text-gray-800">Move candidates to</span>
          <select
            id="delete-move-to"
            required
            value={moveTo}
            onChange={(e) => setMoveTo(e.target.value)}
            className="mt-1.5 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900"
          >
            <option value="">Choose a stage…</option>
            {others.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {/* Dark text on the amber tint: `text-warning` on `bg-warning-bg` is 3.45:1, under AA for 13 px (QA stage 3). */}
      {stage?.system_key && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-warning-bg px-3 py-2 text-[13px] text-gray-800">
          <AlertTriangle size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-warning" aria-hidden />
          <span>
            Automation targets this stage (<code className="mono text-[12px]">{stage.system_key}</code>). After
            deletion, steps that would move candidates here are skipped and logged instead.
          </span>
        </div>
      )}
    </Modal>
  );
}
